import {
  AksiaValidatorFn,
  IValidationService,
  StateEnum,
  TRegisteredValidation,
} from '@aksia/infrastructure';
import { computed, Injectable, isSignal, Signal, signal } from '@angular/core';

@Injectable({
  providedIn: 'root',
})
export class ValidationService implements IValidationService {
  registeredValidations: Map<string, TRegisteredValidation> = new Map<
    string,
    TRegisteredValidation
  >();

  hasErrors = signal(false);
  hasWarnings = signal(false);
  summary = signal('');
  details = signal('');

  state = computed(() =>
    this.hasErrors()
      ? StateEnum.Error
      : this.hasWarnings()
        ? StateEnum.Warning
        : StateEnum.Pristine,
  );

  validationExists(tag: string) {
    return this.registeredValidations.has(tag);
  }

  getValidationState(tag: string): StateEnum {
    if (tag.startsWith('@')) {
      let hasErrors = this.registeredValidations
        .entries()
        ?.some(
          (registeredValidation) =>
            registeredValidation[0].includes(tag) &&
            registeredValidation[1].errors()?.length! > 0,
        );
      if (!hasErrors) {
        let hasWarnings = this.registeredValidations
          .entries()
          ?.some(
            (registeredValidation) =>
              registeredValidation[0].includes(tag) &&
              registeredValidation[1].warnings()?.length! > 0,
          );
        return hasWarnings ? StateEnum.Warning : StateEnum.Pristine;
      } else {
        return StateEnum.Error;
      }
    } else {
      let registeredValidation = this.registeredValidations.get(tag);
      if (registeredValidation?.errors()?.length! > 0) {
        return StateEnum.Error;
      } else if (registeredValidation?.warnings()?.length! > 0) {
        return StateEnum.Warning;
      } else {
        return StateEnum.Pristine;
      }
    }
  }

  getErrorDetailsByTag(tag: string) {
    if (tag.startsWith('@')) {
      let details = [...this.registeredValidations.entries()]
        .filter(
          (registeredValidation) =>
            registeredValidation[0].includes(tag) &&
            registeredValidation[1].errors()?.length! > 0,
        )
        .map((registeredValidation) => {
          return {
            tag: registeredValidation?.[0]?.split('@')?.at(0)!,
            messages: registeredValidation?.[1].errors()!,
          };
        });
      return details;
    } else {
      let registeredValidation = this.registeredValidations.get(tag);
      if (registeredValidation?.errors()?.length! > 0) {
        return [{ tag: tag!, messages: registeredValidation?.errors()! }];
      }
    }
    return [];
  }

  registerValidation(
    tag: string,
    value: Signal<unknown>,
    validators: Array<AksiaValidatorFn>,
    chainTags?: Array<string> | Signal<Array<string>>,
  ) {
    this.registeredValidations.set(tag, {
      value,
      validators,
      chainTags: chainTags ?? [],
      errors: signal([]),
      warnings: signal([]),
    });

    return this.registeredValidations.get(tag)!;
  }

  removeValidation(tag: string) {
    if (tag.startsWith('@')) {
      let tags = this.registeredValidations
        .keys()
        ?.filter((registeredValidation) => registeredValidation.includes(tag));
      tags?.forEach((tag) => {
        this.registeredValidations.delete(tag);
      });
    } else {
      this.registeredValidations.delete(tag);
    }
    this.updateFlags();
  }

  clearValidations() {
    this.registeredValidations.clear();
  }

  updateValidationTag(currentTag: string, newTag: string, reValidate = false) {
    let registeredValidation = this.registeredValidations.get(currentTag);
    if (registeredValidation) {
      this.registeredValidations.set(newTag, registeredValidation!);
      this.registeredValidations.delete(currentTag);
    }
    this.updateFlags();
  }

  validate(tag: string, value: unknown, preventChainValidation = false) {
    let registeredValidation = this.registeredValidations.get(tag);
    registeredValidation?.errors.set([]);
    registeredValidation?.warnings.set([]);
    registeredValidation?.validators.forEach((validator) => {
      let result = validator(registeredValidation.value());
      this.registeredValidations
        .get(tag)
        ?.errors.update((errors) => [...errors, ...result.errors]);
      this.registeredValidations
        .get(tag)
        ?.warnings.update((warnings) => [...warnings, ...result.warnings]);
    });

    if (!preventChainValidation) {
      let chainTags = isSignal(registeredValidation?.chainTags)
        ? registeredValidation?.chainTags()
        : registeredValidation?.chainTags;
      chainTags?.forEach((chainTag) => {
        let chainedValidation = this.registeredValidations.get(chainTag);
        this.validate(chainTag, chainedValidation?.value, true);
      });
    }

    this.updateFlags();

    return registeredValidation!;
  }

  validateAll(tag?: string) {
    if (tag?.startsWith('@')) {
      let tagValidations = this.registeredValidations
        .entries()
        ?.filter((registeredValidation) =>
          registeredValidation[0].includes(tag),
        );

      tagValidations?.forEach((registeredValidation) => {
        this.validate(registeredValidation[0], registeredValidation[1].value());
      });
    }
  }

  private updateFlags() {
    this.hasErrors.set(
      [...this.registeredValidations.entries()].some(
        (registeredValidation) =>
          registeredValidation?.[1].errors()?.length > 0,
      ),
    );

    this.hasWarnings.set(
      [...this.registeredValidations.entries()].some(
        (registeredValidation) =>
          registeredValidation?.[1].warnings()?.length > 0,
      ),
    );

    this.summary.set(
      [...this.registeredValidations.entries()]
        .filter(
          (registeredValidation) =>
            registeredValidation?.[1].warnings()?.length > 0 ||
            registeredValidation?.[1].errors()?.length > 0,
        )
        ?.map(
          (registeredValidation) =>
            `${registeredValidation?.[0]}: ${registeredValidation?.[1].errors()?.length} errors, ${registeredValidation?.[1].warnings()?.length} warnings`,
        )
        ?.join('\n\r'),
    );

    this.details.set(
      [...this.registeredValidations.entries()]
        .filter(
          (registeredValidation) =>
            registeredValidation?.[1].errors()?.length > 0,
        )
        ?.map((registeredValidation) => {
          let details = `<span class="text-size-list-item font-list-item">${registeredValidation?.[0]?.split('@')?.at(0)}</span><span class="text-size-list-item font-list-item text-error-aksia">${registeredValidation?.[1].errors()?.join(', ')}</span>`;
          return details;
        })
        .join(''),
    );
  }
}
